import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useI18n } from '../../i18n';
import { CardBack } from '../art/CardArt';
import { shuffledDeck } from '../draw';
import { playCue } from '../feedback';

interface Props {
    /** One label per card to choose, in order (the spread's positions). */
    positions: string[];
    /** The chosen card ids, in the order of the positions. */
    onConfirm: (ids: string[]) => void;
}

const ROWS = 3;
const PER_ROW = 26;
/** Room above the fan for a chosen card to lift. */
const LIFT = 20;

/**
 * Where each of the 78 cards sits. The cards form three nested arcs, like a hand of cards held open: each
 * card is turned to point away from a centre far below, and the arcs overlap so the whole deck fits in a
 * band about 0.75 of the width tall. Nothing is taller than that, so the page never has to scroll the deck.
 */
export const fanGeometry = (width: number) => {
    const cw = Math.min(0.17 * width, 74);
    const ch = cw * 1.7;
    const radius = 0.8 * width;
    // Shrink the arc until the outermost card, turned and lifted, still fits inside the width.
    let half = Math.asin(Math.min(0.99, (width - cw) / (2 * radius)));
    const reach = (h: number) =>
        radius * Math.sin(h) +
        (cw / 2) * Math.cos(h) +
        (ch / 2) * Math.sin(h) +
        LIFT * 0.8;
    while (half > 0.1 && reach(half) > width / 2 - 4) half -= 0.005;
    const rowStep = ch * 0.5;
    const sag = radius * (1 - Math.cos(half));
    const height = LIFT + ch + sag + rowStep * (ROWS - 1);
    const slot = (index: number) => {
        const row = Math.floor(index / PER_ROW);
        const k = index % PER_ROW;
        const phi = -half + (2 * half * k) / (PER_ROW - 1);
        return {
            x: width / 2 + radius * Math.sin(phi) - cw / 2,
            y: LIFT + row * rowStep + radius * (1 - Math.cos(phi)),
            phi,
        };
    };
    return { cw, ch, height, slot };
};

/**
 * All 78 cards, face down, spread in an arc. The person touches the ones that call to them, one per
 * position. Every card of the deck is on the table, and each can be chosen only once, so a card
 * can never repeat in a spread. Touching a chosen card again puts it back.
 */
export const ChooseDeck: React.FC<Props> = ({ positions, onConfirm }) => {
    const { t } = useI18n();
    // The order is fixed for this visit: the card under each slot does not change as the person picks.
    const deck = useMemo(() => shuffledDeck(), []);
    const [picked, setPicked] = useState<number[]>([]);
    const [open, setOpen] = useState(false);
    const [width, setWidth] = useState(360);
    const box = useRef<HTMLDivElement>(null);
    const full = picked.length === positions.length;
    const geo = useMemo(() => fanGeometry(width), [width]);

    useEffect(() => {
        const el = box.current;
        if (!el) return;
        const measure = () => setWidth(Math.round(el.clientWidth) || 360);
        measure();
        const observer =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(measure)
                : null;
        observer?.observe(el);
        // The cards start in one pile and fan out on the next frame.
        const id = window.setTimeout(() => setOpen(true), 60);
        return () => {
            observer?.disconnect();
            window.clearTimeout(id);
        };
    }, []);

    const toggle = (slot: number) => {
        setPicked((prev) => {
            if (prev.includes(slot)) {
                playCue('ui');
                return prev.filter((s) => s !== slot);
            }
            if (prev.length >= positions.length) return prev;
            playCue('flip');
            return [...prev, slot];
        });
    };

    return (
        <Stack alignItems="center" spacing={2} sx={{ width: '100%' }}>
            <Typography variant="h5" component="h2">
                {t('ritual.choose.title')}
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 420 }}>
                {positions.length === 1
                    ? t('ritual.choose.hintOne')
                    : t('ritual.choose.hintMany', { count: positions.length })}
            </Typography>
            <Stack
                direction="row"
                flexWrap="wrap"
                justifyContent="center"
                sx={{ gap: 0.75 }}
            >
                {positions.map((label, i) => (
                    <Chip
                        key={label + i}
                        size="small"
                        label={label}
                        color={picked[i] !== undefined ? 'primary' : 'default'}
                        variant={
                            picked[i] !== undefined ? 'filled' : 'outlined'
                        }
                    />
                ))}
            </Stack>
            <Box
                ref={box}
                role="group"
                aria-label={t('ritual.choose.title')}
                sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 480,
                    height: geo.height,
                    my: 1,
                }}
            >
                {deck.map((id, slot) => {
                    const order = picked.indexOf(slot);
                    const chosen = order >= 0;
                    const { x, y, phi } = geo.slot(slot);
                    // A chosen card slides out along its own radius, away from the arc's centre.
                    const lx = chosen ? Math.sin(phi) * LIFT : 0;
                    const ly = chosen ? -Math.cos(phi) * LIFT : 0;
                    const cx = width / 2 - geo.cw / 2;
                    const cy = LIFT + geo.height * 0.3;
                    return (
                        <Box
                            key={id}
                            component="button"
                            type="button"
                            onClick={() => toggle(slot)}
                            aria-pressed={chosen}
                            aria-label={t('ritual.choose.card', {
                                n: slot + 1,
                            })}
                            data-testid="deck-card"
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                width: geo.cw,
                                height: geo.ch,
                                p: 0,
                                border: 0,
                                background: 'none',
                                cursor: 'pointer',
                                borderRadius: '5px',
                                zIndex: chosen ? 200 + order : slot,
                                transform: open
                                    ? `translate(${x + lx}px, ${y + ly}px) rotate(${phi}rad) scale(${chosen ? 1.08 : 1})`
                                    : `translate(${cx}px, ${cy}px)`,
                                transition: `transform .7s cubic-bezier(.2,.8,.2,1) ${open ? Math.min(slot * 9, 500) : 0}ms, filter .25s`,
                                filter: chosen
                                    ? 'drop-shadow(0 0 8px rgba(212,175,106,.95))'
                                    : 'drop-shadow(0 1px 2px rgba(0,0,0,.7))',
                                '@media (prefers-reduced-motion: reduce)': {
                                    transition: 'none',
                                },
                            }}
                        >
                            <CardBack />
                            {chosen && (
                                <Box
                                    component="span"
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontFamily: 'Cinzel, serif',
                                        fontWeight: 700,
                                        fontSize: 20,
                                        color: '#120a1f',
                                        textShadow:
                                            '0 0 6px rgba(255,236,190,.9)',
                                        background: 'rgba(212,175,106,.78)',
                                        borderRadius: '5px',
                                    }}
                                >
                                    {order + 1}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
            <Button
                variant="contained"
                size="large"
                disabled={!full}
                onClick={() => {
                    playCue('swell');
                    onConfirm(picked.map((slot) => deck[slot]));
                }}
            >
                {t('ritual.choose.confirm')}
            </Button>
        </Stack>
    );
};
